
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AuthLayout from "@/components/layouts/AuthLayout";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [passwordType, setPasswordType] = useState<'invite' | 'reset'>('invite');

  // Create schema for password validation
  const formSchema = z.object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(100, "Password is too long")
      .refine(
        (password) => /[A-Z]/.test(password),
        "Password must contain at least one uppercase letter"
      )
      .refine(
        (password) => /[0-9]/.test(password),
        "Password must contain at least one number"
      )
      .refine(
        (password) => /[^A-Za-z0-9]/.test(password),
        "Password must contain at least one special character"
      ),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

  // Initialize form with react-hook-form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    // Only run once to avoid re-initialization issues
    if (hasInitialized) {
      console.log("Skipping re-initialization");
      return;
    }

    setHasInitialized(true);
    console.log("SetPassword initialization started");
    
    const extractAndVerifyToken = async () => {
      try {
        // Store the URL at the moment of initialization
        const fullUrl = window.location.href;
        console.log("Processing URL:", fullUrl);
        
        // Check if this is a direct Supabase auth redirect with token in URL search params
        const searchParams = new URLSearchParams(location.search);
        const urlToken = searchParams.get('token');
        
        if (urlToken) {
          console.log("Found token in URL search params");
          
          // For direct Supabase links, verify and exchange the token
          const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: urlToken,
            type: 'recovery',
          });
          
          if (verifyError) {
            console.error("Token verification failed:", verifyError);
            setInitializationError("Invalid or expired recovery link");
            setVerifying(false);
            throw verifyError;
          }
          
          console.log("OTP verification successful:", verifyData);
          
          if (verifyData.session) {
            setAccessToken(verifyData.session.access_token);
            setRefreshToken(verifyData.session.refresh_token);
            console.log("Set tokens from verified session");
            setPasswordType('reset');
            setVerifying(false);
          } else {
            throw new Error("No session returned from verification");
          }
          
          return;
        }
        
        // Extract token from location.hash (Supabase client-side redirects)
        const hash = location.hash;
        
        if (hash && hash.includes('access_token=')) {
          console.log("Found hash with access_token");
          const accessTokenMatch = hash.match(/access_token=([^&]*)/);
          const refreshTokenMatch = hash.match(/refresh_token=([^&]*)/);
          const typeMatch = hash.match(/type=([^&]*)/);
          
          // Determine if this is a password reset or invitation
          if (typeMatch && typeMatch[1]) {
            setPasswordType(typeMatch[1] === 'recovery' ? 'reset' : 'invite');
            console.log(`Token type: ${typeMatch[1]}, setting mode to ${typeMatch[1] === 'recovery' ? 'reset' : 'invite'}`);
          }
          
          if (accessTokenMatch && accessTokenMatch[1]) {
            const token = accessTokenMatch[1];
            console.log("Token extracted from URL hash");
            
            let refresh = null;
            if (refreshTokenMatch && refreshTokenMatch[1]) {
              refresh = refreshTokenMatch[1];
              console.log("Refresh token extracted from URL hash");
            }
            
            // Save tokens
            setAccessToken(token);
            if (refresh) setRefreshToken(refresh);
            
            // Verify the token by trying to get the user
            console.log("Verifying token...");
            const { data, error } = await supabase.auth.getUser(token);
            
            if (error) {
              console.error("Token verification failed:", error.message);
              setInitializationError("Invalid or expired link");
              setVerifying(false);
              throw error;
            }
            
            console.log("Token valid, found user:", data.user?.email);
            
            // Important: Set the session with both access and refresh tokens
            if (refresh) {
              const { error: sessionError } = await supabase.auth.setSession({
                access_token: token,
                refresh_token: refresh,
              });
              
              if (sessionError) {
                console.error("Error setting session:", sessionError.message);
                throw sessionError;
              }
              console.log("Session successfully set with both tokens");
            }
            
            setVerifying(false);
          } else {
            console.error("Could not extract access_token from hash");
            throw new Error("Invalid token format");
          }
        } else {
          console.error("No access_token found in URL hash or search params");
          throw new Error("No token found in URL. This may be because the URL was improperly formed or the token has expired.");
        }
      } catch (error: any) {
        console.error("Initialization error:", error);
        setInitializationError(error.message || "Invalid link");
        setVerifying(false);
        
        // Show error toast and redirect after a brief delay
        toast({
          title: "Invalid or expired link",
          description: "Please request a new password reset link.",
          variant: "destructive",
        });
        
        setTimeout(() => navigate("/auth/login"), 3000);
      }
    };

    extractAndVerifyToken();
  }, [location, navigate, toast, hasInitialized]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log("Form submitted with values:", values);
    setLoading(true);
    try {
      if (!accessToken) {
        throw new Error("No access token available");
      }

      console.log("Updating user with new password");
      // Update user with new password and set email_verified to true
      const { data, error } = await supabase.auth.updateUser({ 
        password: values.password,
        data: {
          email_verified: true
        }
      });

      if (error) {
        console.error("Update user error:", error);
        throw error;
      }

      console.log("Password set successfully");
      
      // Now update the is_email_confirmed flag in the users table if this is an invitation
      if (passwordType === 'invite') {
        const { data: authUser } = await supabase.auth.getUser();
        if (authUser?.user) {
          const { error: updateError } = await supabase.functions.invoke('users', {
            body: { 
              action: 'activate-user',
              authUserId: authUser.user.id
            },
            method: 'POST'
          });
          
          if (updateError) {
            console.error("Error updating user status:", updateError);
            // Continue with the flow even if this update fails
          } else {
            console.log("User status updated to active");
          }
        }
      }

      toast({
        title: passwordType === 'invite' ? "Account activated successfully" : "Password reset successfully",
        description: passwordType === 'invite' 
          ? "Your account has been activated. Welcome to the platform!" 
          : "Your password has been updated successfully.",
      });

      // Set session using both access and refresh tokens before redirecting
      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (sessionError) {
          console.error("Error setting session:", sessionError);
          throw sessionError;
        }
        console.log("Session successfully maintained");
      }

      // Redirect to backoffice dashboard as a logged-in user
      navigate("/backoffice/dashboard");
    } catch (error: any) {
      console.error("Error setting password:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to set password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = passwordType === 'invite' ? "Set Your Password" : "Reset Your Password";
  const pageSubtitle = passwordType === 'invite' 
    ? "Create a password for your account" 
    : "Create a new password for your account";

  return (
    <AuthLayout title={pageTitle} subtitle={pageSubtitle}>
      {verifying ? (
        <div className="flex flex-col items-center justify-center p-8 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <p className="text-sm text-center text-muted-foreground">
            Verifying your link...
          </p>
        </div>
      ) : initializationError ? (
        <div className="flex flex-col items-center justify-center p-8 space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{initializationError}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate("/auth/login")}>
            Return to Login
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="********"
                      disabled={loading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters and include uppercase, number, and special character.
                  </p>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="********"
                      disabled={loading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Password...
                </>
              ) : (
                passwordType === 'invite' ? "Activate Account" : "Reset Password"
              )}
            </Button>
          </form>
        </Form>
      )}
    </AuthLayout>
  );
};

export default SetPasswordPage;
